import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import {
  FileImageOutlined,
  CloseOutlined
} from '@ant-design/icons';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff'];
const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff'];

export const ImageUploadField = ({
  field,
  value = [],
  onChange,
  onRemove,
  error,
  uploading,
  maxFiles
}) => {
  const [validationError, setValidationError] = useState('');

  const validateImage = (file) => {
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const isValidExtension = ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension);
    const isValidType = ALLOWED_IMAGE_TYPES.includes(file.type);

    if (!isValidExtension && !isValidType) {
      return `Geçersiz görsel formatı: ${file.name}. Desteklenen formatlar: ${ALLOWED_IMAGE_EXTENSIONS.join(', ')}`;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB
      return `Görsel boyutu çok büyük: ${file.name}. Maksimum görsel boyutu: 10MB`;
    }

    return '';
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    let hasError = false;
    let errorMessage = '';

    // Dosya sayısı kontrolü
    if (value.length + files.length > maxFiles) {
      setValidationError(`En fazla ${maxFiles} görsel yükleyebilirsiniz`);
      return;
    }

    // Her dosyayı kontrol et
    for (const file of files) {
      const validationResult = validateImage(file);
      if (validationResult) {
        hasError = true;
        errorMessage = validationResult;
        break;
      }
    }

    if (hasError) {
      setValidationError(errorMessage);
      return;
    }

    setValidationError('');
    onChange(files);
  };

  return (
    <FormControl error={!!error || !!validationError} required={field.required} fullWidth className="form-preview-image-field">
      <Box className="form-preview-image-upload">
        <Button
          variant="outlined"
          component="label"
          size="medium"
          startIcon={<FileImageOutlined />}
          disabled={uploading}
          sx={{
            width: '100%',
            height: '120px',
            border: '2px dashed',
            borderColor: 'primary.main',
            bgcolor: 'background.paper',
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'primary.dark'
            }
          }}
        >
          <Stack spacing={1} alignItems="center">
            <FileImageOutlined sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="body2" color="primary">
              {uploading ? 'Yükleniyor...' : 'Görsel Yüklemek İçin Tıklayın'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              veya sürükleyip bırakın
            </Typography>
          </Stack>
          <input
            type="file"
            hidden
            multiple
            accept={ALLOWED_IMAGE_EXTENSIONS.join(',')}
            onChange={handleFileChange}
          />
        </Button>

      {/*   <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Desteklenen formatlar: JPG, PNG, GIF, WEBP, BMP, TIFF
          <br />
          Maksimum görsel sayısı: {maxFiles}
          <br />
          Maksimum görsel boyutu: 10MB
        </Typography> */}

        {value.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Yüklenen Görseller ({value.length}/{maxFiles}):
            </Typography>
            <Grid container spacing={2}>
              {value.map((file, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    sx={{
                      p: 1,
                      position: 'relative',
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ position: 'relative', paddingTop: '75%' }}>
                      <img
                        src={file.base64}
                        alt={file.name}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '80%' }}>
                        {file.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => onRemove(index)}
                        disabled={uploading}
                        sx={{
                          color: 'error.main',
                          '&:hover': {
                            bgcolor: 'error.lighter'
                          }
                        }}
                      >
                        <CloseOutlined fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {(error || validationError) && (
          <FormHelperText error sx={{ mt: 1 }}>
            {error || validationError}
          </FormHelperText>
        )}
      </Box>
    </FormControl>
  );
}; 